(() => {
  if (customElements.get('wc-split-carousel')) return;

  class WcSplitCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.boundResize = this.debounce(this.handleResize.bind(this), 150);
      this.copyHandler = this.handleCopyClick.bind(this);
      this.blockSelectHandler = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.sectionElement = this.closest('[data-wc-split-carousel-section]');
      this.swiperElement = this.querySelector('.wc-split__swiper');
      this.prevEl = this.querySelector('.wc-split__nav--prev');
      this.nextEl = this.querySelector('.wc-split__nav--next');
      this.paginationEl = this.querySelector('.wc-split__pagination');
      this.mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint || '767', 10);
      this.enableLoop = this.dataset.loop === 'true';

      this.bindCopyButtons();
      document.addEventListener('shopify:block:select', this.blockSelectHandler);

      this.waitForSwiper(() => {
        this.handleResize();
        window.addEventListener('resize', this.boundResize, { passive: true });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundResize);
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      this.unbindCopyButtons();
      this.destroySwiper();
    }

    debounce(fn, wait) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
      };
    }

    waitForSwiper(callback, retry = 0) {
      if (typeof window.Swiper === 'function') {
        callback();
        return;
      }
      if (retry > 20) return;
      setTimeout(() => this.waitForSwiper(callback, retry + 1), 120);
    }

    bindCopyButtons() {
      const root = this.sectionElement || this.closest('section');
      if (!root) return;

      root.querySelectorAll('[data-wc-copy-code]').forEach((button) => {
        button.addEventListener('click', this.copyHandler);
      });
    }

    unbindCopyButtons() {
      const root = this.sectionElement || this.closest('section');
      if (!root) return;

      root.querySelectorAll('[data-wc-copy-code]').forEach((button) => {
        button.removeEventListener('click', this.copyHandler);
      });
    }

    handleCopyClick(event) {
      const button = event.currentTarget;
      const code = button.dataset.wcCopyCode;
      if (!code) return;

      const copiedLabel = button.dataset.copiedLabel || 'Copied!';
      const defaultLabel = button.textContent.trim();

      const onSuccess = () => {
        button.classList.add('is-copied');
        button.textContent = copiedLabel;
        window.setTimeout(() => {
          button.classList.remove('is-copied');
          button.textContent = defaultLabel;
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(onSuccess).catch(() => {});
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand('copy');
        onSuccess();
      } catch (error) {
        /* noop */
      }

      document.body.removeChild(textarea);
    }

    handleBlockSelect(event) {
      if (!this.sectionElement || !this.swiperInstance) return;
      if (event.detail.sectionId !== this.dataset.sectionId) return;

      const blockId = event.detail.blockId;
      const slides = Array.from(this.querySelectorAll('.wc-split__slide[data-block-id]'));
      const index = slides.findIndex((slide) => slide.dataset.blockId === blockId);

      if (index >= 0) {
        this.swiperInstance.slideToLoop ? this.swiperInstance.slideToLoop(index) : this.swiperInstance.slideTo(index);
      }
    }

    handleResize() {
      if (!this.swiperElement) return;

      if (this.swiperInstance) {
        this.swiperInstance.update();
        return;
      }

      this.initSwiper();
    }

    initSwiper() {
      if (this.swiperInstance || typeof window.Swiper !== 'function') return;

      const slideCount = this.querySelectorAll('.wc-split__slide').length;
      if (slideCount <= 1) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const config = {
        slidesPerView: 1,
        spaceBetween: 0,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        watchOverflow: true,
        speed: prefersReducedMotion ? 0 : 600,
        loop: this.enableLoop && slideCount > 1,
        grabCursor: true
      };

      if (this.prevEl && this.nextEl) {
        config.navigation = {
          prevEl: this.prevEl,
          nextEl: this.nextEl
        };
      }

      if (this.paginationEl) {
        config.pagination = {
          el: this.paginationEl,
          clickable: true
        };
      }

      this.swiperInstance = new window.Swiper(this.swiperElement, config);
    }

    destroySwiper() {
      if (!this.swiperInstance) return;
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  customElements.define('wc-split-carousel', WcSplitCarousel);
})();
